import type { ConditionalVisibility, Field, FormSection } from 'korint-sdk/react';
import _ from 'lodash';

/**
 * Helper function to normalize values for comparison
 */
export function normalizeValueForComparison(value: unknown, compareWith: unknown): unknown {
  // If comparing with a number, try to parse the value as a number
  if (typeof compareWith === 'number' && typeof value === 'string') {
    const parsedValue = Number.parseFloat(value);
    return Number.isNaN(parsedValue) ? value : parsedValue;
  }
  // A BOOLEAN field holds 'true'/'false' when rendered as a radio/toggle, a real boolean as a checkbox
  if (typeof compareWith === 'boolean' && (value === 'true' || value === 'false')) {
    return value === 'true';
  }
  return value;
}

/**
 * Evaluates a conditional visibility rule against a given value
 */
export function evaluateCondition(condition: ConditionalVisibility, sourceValue: unknown): boolean {
  switch (condition.condition) {
    case 'equals': {
      const normalizedValue = normalizeValueForComparison(sourceValue, condition.value);
      return normalizedValue === condition.value;
    }
    case 'notEquals': {
      const normalizedValue = normalizeValueForComparison(sourceValue, condition.value);
      return normalizedValue !== condition.value;
    }
    case 'exists':
      return sourceValue !== undefined && sourceValue !== null && sourceValue !== '';
    case 'notExists':
      return sourceValue === undefined || sourceValue === null || sourceValue === '';
    case 'in': {
      if (!condition.values?.length) return false;

      // Normalize source value against the first element to determine target type
      const normalizedSourceValue = normalizeValueForComparison(sourceValue, condition.values[0]);

      // Normalize each value in the array for consistent comparison
      const normalizedValues = condition.values.map(val =>
        normalizeValueForComparison(val, condition.values![0]),
      );

      return normalizedValues.includes(normalizedSourceValue);
    }
    case 'notIn': {
      if (!condition.values?.length) return true;

      // Normalize source value against the first element to determine target type
      const normalizedSourceValue = normalizeValueForComparison(sourceValue, condition.values[0]);

      // Normalize each value in the array for consistent comparison
      const normalizedValues = condition.values.map(val =>
        normalizeValueForComparison(val, condition.values![0]),
      );

      return !normalizedValues.includes(normalizedSourceValue);
    }
    case 'greaterThan': {
      const normalizedValue = normalizeValueForComparison(sourceValue, condition.value);
      return (
        typeof normalizedValue === 'number' &&
        typeof condition.value === 'number' &&
        normalizedValue > condition.value
      );
    }
    case 'lessThan': {
      const normalizedValue = normalizeValueForComparison(sourceValue, condition.value);
      return (
        typeof normalizedValue === 'number' &&
        typeof condition.value === 'number' &&
        normalizedValue < condition.value
      );
    }
    case 'greaterThanOrEquals': {
      const normalizedValue = normalizeValueForComparison(sourceValue, condition.value);
      return (
        typeof normalizedValue === 'number' &&
        typeof condition.value === 'number' &&
        normalizedValue >= condition.value
      );
    }
    case 'lessThanOrEquals': {
      const normalizedValue = normalizeValueForComparison(sourceValue, condition.value);
      return (
        typeof normalizedValue === 'number' &&
        typeof condition.value === 'number' &&
        normalizedValue <= condition.value
      );
    }
    case 'dateOlderThanYears':
    case 'dateYoungerThanYears': {
      if (typeof condition.value !== 'number') return false;
      // When the date is missing/invalid, assume "not old enough" — this keeps
      // dateOlderThanYears → false (restrictive) and dateYoungerThanYears → true (permissive),
      // so non-senior fields remain visible as a safe default.
      if (typeof sourceValue !== 'string') return condition.condition === 'dateYoungerThanYears';
      const [year, month, day] = sourceValue.split('T')[0].split('-').map(Number);
      const birthDate = new Date(year, month - 1, day);
      if (
        !Number.isInteger(year) ||
        !Number.isInteger(month) ||
        !Number.isInteger(day) ||
        birthDate.getFullYear() !== year ||
        birthDate.getMonth() !== month - 1 ||
        birthDate.getDate() !== day
      ) {
        return condition.condition === 'dateYoungerThanYears';
      }
      const now = new Date();
      let age = now.getFullYear() - birthDate.getFullYear();
      const monthDiff = now.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birthDate.getDate())) {
        age--;
      }
      return condition.condition === 'dateOlderThanYears'
        ? age >= condition.value
        : age < condition.value;
    }
    default:
      return true;
  }
}

/**
 * Evaluate if a field should be visible based on its conditional visibility settings
 */
export function isFieldVisible(
  field: Field,
  formValues: Record<string, unknown>,
  externalData?: Record<string, unknown>,
): boolean {
  if (!field.showWhen || field.showWhen.length === 0) {
    return true;
  }

  // Evaluate all conditions - ALL must be true for the field to be visible (AND logic)
  return field.showWhen.every(condition => {
    const propertyName = condition.fieldName || condition.apiProperty;

    if (!propertyName) {
      return true;
    }

    let sourceValue: unknown;

    if (condition.fieldName) {
      sourceValue = _.get(formValues, condition.fieldName);
    } else if (condition.apiProperty && externalData) {
      sourceValue = _.get(externalData, condition.apiProperty);
    } else {
      sourceValue = _.get(formValues, propertyName);
    }

    return evaluateCondition(condition, sourceValue);
  });
}

/**
 * Evaluate if a section should be visible based on its conditional visibility settings
 */
export function isSectionVisible(
  section: FormSection,
  formValues: Record<string, unknown>,
  externalData?: Record<string, unknown>,
): boolean {
  if (!section.showWhen || section.showWhen.length === 0) {
    return true;
  }

  // Evaluate all conditions - ALL must be true for the section to be visible (AND logic)
  return section.showWhen.every(condition => {
    const propertyName = condition.fieldName || condition.apiProperty;

    if (!propertyName) {
      return true;
    }

    // Determine data source based on which property is used
    let sourceValue: unknown;

    if (condition.fieldName) {
      sourceValue = formValues[condition.fieldName];
    } else if (condition.apiProperty && externalData) {
      // Handle nested API properties (e.g., 'customFields.someKey')
      sourceValue = _.get(externalData, condition.apiProperty);
    } else {
      return true;
    }

    return evaluateCondition(condition, sourceValue);
  });
}
